import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ConnectionCostNotice } from '../components/credits/ConnectionCostNotice';
import { useCreditConfirm } from '../components/credits/useCreditConfirm';

const API_BASE_URL = '/api';

interface Platform {
  id: string;
  name: string;
  icon: string;
  supportsInbox: boolean;
  supportsComments: boolean;
  supportsReviews: boolean;
  supportsPosting?: boolean;
  supportsAnalytics?: boolean;
  authType: string;
  requiresSelection: boolean;
  selectionType?: string;
  description: string;
  // Instagram e a unica plataforma com dois caminhos de OAuth. Quando vier
  // preenchido, a tela pergunta por qual conectar antes de mandar pro Meta.
  loginMethods?: LoginMethod[];
  defaultLoginMethod?: string;
}

interface LoginMethod {
  id: string;
  label: string;
  recommended?: boolean;
  requiresSelection: boolean;
  selectionType: string | null;
  description: string;
}

interface Connection {
  id: string;
  tenantId: string;
  platform: string;
  platformAccountId: string | null;
  profileId: string | null;
  accountName: string | null;
  accountUsername: string | null;
  accountAvatar: string | null;
  status: string;
  lastChargedAt: string | null;
  nextChargeAt: string | null;
  createdAt: string;
}

interface SelectionOption {
  id: string;
  name: string;
  avatar: string | null;
  category: string | null;
  // Instagram: Pagina sem conta profissional vinculada nao conecta nada.
  hasInstagram?: boolean;
}

const PLATFORM_COLORS: Record<string, { bg: string; text: string; border: string; icon: string }> = {
  instagram: { bg: 'bg-gradient-to-r from-purple-500 to-pink-500', text: 'text-white', border: 'border-pink-300', icon: '#E4405F' },
  facebook: { bg: 'bg-blue-600', text: 'text-white', border: 'border-blue-300', icon: '#1877F2' },
  twitter: { bg: 'bg-black', text: 'text-white', border: 'border-gray-400', icon: '#000000' },
  youtube: { bg: 'bg-red-600', text: 'text-white', border: 'border-red-300', icon: '#FF0000' },
  linkedin: { bg: 'bg-blue-700', text: 'text-white', border: 'border-blue-400', icon: '#0A66C2' },
  tiktok: { bg: 'bg-black', text: 'text-white', border: 'border-gray-400', icon: '#000000' },
  reddit: { bg: 'bg-orange-600', text: 'text-white', border: 'border-orange-300', icon: '#FF4500' },
  bluesky: { bg: 'bg-sky-500', text: 'text-white', border: 'border-sky-300', icon: '#0085FF' },
  threads: { bg: 'bg-black', text: 'text-white', border: 'border-gray-400', icon: '#000000' },
  googlebusiness: { bg: 'bg-green-600', text: 'text-white', border: 'border-green-300', icon: '#4285F4' },
  telegram: { bg: 'bg-sky-500', text: 'text-white', border: 'border-sky-300', icon: '#26A5E4' },
  pinterest: { bg: 'bg-red-600', text: 'text-white', border: 'border-red-300', icon: '#E60023' },
  snapchat: { bg: 'bg-yellow-400', text: 'text-black', border: 'border-yellow-200', icon: '#FFFC00' },
  whatsapp: { bg: 'bg-green-500', text: 'text-white', border: 'border-green-300', icon: '#25D366' },
};

function PlatformIcon({ platform, size = 24 }: { platform: string; size?: number }) {
  const icons: Record<string, JSX.Element> = {
    instagram: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
      </svg>
    ),
    facebook: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    twitter: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    youtube: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    linkedin: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    tiktok: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
      </svg>
    ),
    reddit: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.785 2.627 3.6 3.476 6.178 3.126-4.443.766-8.328 2.66-3.18 9.394C7.997 20.846 11.167 16.5 12 14.18c.833 2.32 3.955 6.613 8.378 8.587 5.148-6.734 1.263-8.628-3.18-9.394 2.578.35 5.393-.499 6.178-3.126C23.622 9.418 24 4.458 24 3.768c0-.69-.139-1.861-.902-2.203-.659-.3-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z"/>
      </svg>
    ),
    bluesky: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.785 2.627 3.6 3.476 6.178 3.126-4.443.766-8.328 2.66-3.18 9.394C7.997 20.846 11.167 16.5 12 14.18c.833 2.32 3.955 6.613 8.378 8.587 5.148-6.734 1.263-8.628-3.18-9.394 2.578.35 5.393-.499 6.178-3.126C23.622 9.418 24 4.458 24 3.768c0-.69-.139-1.861-.902-2.203-.659-.3-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z"/>
      </svg>
    ),
    threads: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.187.408-2.26 1.33-3.017.88-.724 2.104-1.138 3.542-1.199 1.048-.044 2.025.06 2.926.31-.075-1.313-.556-2.288-1.45-2.864-.965-.622-2.26-.77-3.453-.396l-.61-1.903c1.584-.508 3.396-.333 4.835.467 1.27.706 2.134 1.878 2.534 3.455.375-.107.77-.192 1.186-.25l.017-.003c1.44-.193 2.806.01 3.818.716.988.688 1.652 1.752 1.928 3.084.437 2.107-.124 4.58-2.36 6.772C18.539 23.058 15.697 23.974 12.186 24zm.06-9.963c-1.07.044-1.91.315-2.432.784-.49.44-.7 1.003-.67 1.773.027.712.335 1.25.916 1.627.64.414 1.47.58 2.344.536 1.135-.061 1.98-.473 2.58-1.22.478-.596.8-1.39.957-2.358-.82-.26-1.74-.393-2.695-.393-.335 0-.67.017-1 .05z"/>
      </svg>
    ),
    whatsapp: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.885-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
      </svg>
    ),
    googlebusiness: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
    ),
    telegram: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
      </svg>
    ),
    pinterest: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.951-7.252 4.184 0 7.426 2.981 7.426 6.96 0 4.156-2.626 7.502-6.273 7.502-1.22 0-2.373-.633-2.763-1.385l-.752 2.868c-.27 1.027-1.006 2.31-1.503 3.093 1.164.36 2.416.554 3.725.554 6.621 0 11.987-5.367 11.987-11.987C24 5.367 18.638 0 12.017 0z"/>
      </svg>
    ),
    snapchat: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.996 0a8.134 8.134 0 0 0-4.636 1.488 7.37 7.37 0 0 0-2.747 4.095c-.328 1.401-.39 2.923-.195 4.348-.12.164-.265.311-.424.437-.323.255-.713.419-1.121.488-.337.057-.68.04-1.009-.047-.13-.035-.259-.076-.388-.119-.481-.161-1.026-.051-1.425.29-.399.34-.582.866-.48 1.378.096.485.421.897.868 1.1.203.091.416.166.635.22.485.122.986.161 1.483.118a3.178 3.178 0 0 0 1.258-.334c.058 1.203.491 2.361 1.238 3.28.375.462.822.86 1.323 1.18.528.336 1.085.617 1.666.837a6.223 6.223 0 0 1-1.005.811 5.923 5.923 0 0 1-1.258.552 6.002 6.002 0 0 1-1.39.243c-.476.03-.956.012-1.428-.052-.519-.071-.979-.317-1.346-.713-.367-.396-.582-.916-.628-1.446-.048-.553.078-1.107.359-1.577a.627.627 0 0 0 .071-.247c.01-.137-.024-.275-.094-.388a.653.653 0 0 0-.244-.225.666.666 0 0 0-.324-.07c-.126.002-.25.038-.36.104-.378.226-.643.606-.74 1.031-.097.426-.002.871.261 1.229.475.648 1.146 1.108 1.897 1.302.736.19 1.503.224 2.253.097 1.19-.201 2.33-.679 3.32-1.401a5.614 5.614 0 0 0 1.572-1.583 6.062 6.062 0 0 0 .564-1.111c.216.516.486 1.008.804 1.464a7.027 7.027 0 0 0 3.013 2.181c.854.341 1.764.512 2.68.503.882-.008 1.751-.186 2.563-.524a4.437 4.437 0 0 0 1.439-.966c.381-.383.651-.853.784-1.365.133-.512.115-1.045-.052-1.547a1.696 1.696 0 0 0-1.155-1.096c-.469-.136-.967-.091-1.417.127a1.674 1.674 0 0 0-.824.931c-.139.349-.158.73-.056 1.087a1.76 1.76 0 0 0 .428.777c.404.453 1.01.656 1.599.535a.862.862 0 0 1 .374.011c.143.037.266.115.352.222.086.108.132.242.133.379.001.122-.036.242-.104.343a.591.591 0 0 1-.225.19c-.382.181-.806.26-1.233.228a4.938 4.938 0 0 1-1.897-.565 5.603 5.603 0 0 1-2.071-1.745c-.266-.37-.48-.771-.637-1.192a5.414 5.414 0 0 1-.284-1.442c.866.273 1.79.351 2.7.228.694-.094 1.36-.33 1.968-.698.411-.25.753-.604.986-1.018a1.996 1.996 0 0 0 .19-1.445c-.139-.472-.444-.88-.865-1.159a2.039 2.039 0 0 0-1.229-.297c-.361.026-.713.116-1.036.265-.456.211-.968.272-1.472.176-.328-.063-.64-.176-.928-.337a2.628 2.628 0 0 1-1.02-.953c-.352-.53-.559-1.139-.6-1.774.127-.887.051-1.787-.222-2.646a7.483 7.483 0 0 0-2.316-3.411A7.632 7.632 0 0 0 11.996 0z"/>
      </svg>
    ),
  };

  return icons[platform] || (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
    </svg>
  );
}

interface Group {
  id: string;
  tenantId: string;
  name: string;
  lateProfileId: string | null;
  status: string;
  lastChargedAt: string | null;
  nextChargeAt: string | null;
  connections: Connection[];
  createdAt: string;
}

export function SocialConnectionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [monthlyCost, setMonthlyCost] = useState(54);
  // 'per_group' (legado, 54/grupo) ou 'per_connection' (45/mês por conexão WhatsApp oficial)
  const [billingModel, setBillingModel] = useState<'per_group' | 'per_connection'>('per_group');
  const [officialConnCost, setOfficialConnCost] = useState(45);
  const [syncing, setSyncing] = useState(false);
  const { request: requestConnectConfirm, modal: connectConfirmModal } = useCreditConfirm();

  // Group management
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState<string | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroupName, setEditingGroupName] = useState('');

  // Connect modal - select group + platform
  const [showConnectModal, setShowConnectModal] = useState(false);
  // Escolha do caminho de OAuth (Instagram). `loginMethodPlatform` != null abre o modal.
  const [loginMethodPlatform, setLoginMethodPlatform] = useState<Platform | null>(null);
  const [loginMethodGroupId, setLoginMethodGroupId] = useState<string | null>(null);
  const [loginMethodChoice, setLoginMethodChoice] = useState<string | null>(null);
  // Conexao que a nova vai substituir (fluxo "trocar forma de conexao"). Null = conexao nova.
  const [loginMethodReplaceId, setLoginMethodReplaceId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  // Bluesky modal
  const [showBlueskyModal, setShowBlueskyModal] = useState(false);
  const [blueskyIdentifier, setBlueskyIdentifier] = useState('');
  const [blueskyPassword, setBlueskyPassword] = useState('');
  const [blueskyGroupId, setBlueskyGroupId] = useState<string | null>(null);

  // Telegram modal
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [telegramGroupId, setTelegramGroupId] = useState<string | null>(null);
  const [telegramCode, setTelegramCode] = useState('');
  const [telegramBotUsername, setTelegramBotUsername] = useState('');
  const [telegramInstructions, setTelegramInstructions] = useState<string[]>([]);
  const [telegramPolling, setTelegramPolling] = useState(false);
  const [telegramConnected, setTelegramConnected] = useState(false);
  const [telegramChatId, setTelegramChatId] = useState('');
  const [telegramTab, setTelegramTab] = useState<'code' | 'chatid'>('code');

  // WhatsApp modal
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsAppGroupId, setWhatsAppGroupId] = useState<string | null>(null);
  const [whatsAppTab, setWhatsAppTab] = useState<'redirect' | 'credentials'>('redirect');
  // Onde o número está hoje. Decide qual Embedded Signup a Meta abre — e a escolha
  // errada leva o usuário para uma tela que não lista o número dele. Sem default:
  // ele precisa dizer, porque só ele sabe.
  const [whatsAppOnboarding, setWhatsAppOnboarding] = useState<'api' | 'business_app' | null>(null);
  const [whatsAppCredentials, setWhatsAppCredentials] = useState({ accessToken: '', wabaId: '', phoneNumberId: '' });
  // Selection modal (Facebook Pages, LinkedIn Orgs, etc)
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [selectionOptions, setSelectionOptions] = useState<SelectionOption[]>([]);
  const [selectionConnectionId, setSelectionConnectionId] = useState<string | null>(null);
  const [selectionPlatform, setSelectionPlatform] = useState<string>('');
  const [loadingSelection, setLoadingSelection] = useState(false);

  const token = localStorage.getItem('auth_token');
  const headers = useMemo<Record<string, string>>(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }), [token]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [platformsRes, groupsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/social-connections/platforms`, { headers }),
        fetch(`${API_BASE_URL}/social-connections/groups`, { headers }),
      ]);

      const platformsData = await platformsRes.json();
      const groupsData = await groupsRes.json();
      if (platformsData.success) setPlatforms(platformsData.data);
      if (groupsData.success) {
        setGroups(groupsData.data);
        setMonthlyCost(groupsData.monthlyCost ?? 54);
        if (groupsData.billingModel) setBillingModel(groupsData.billingModel);
        if (groupsData.officialConnectionMonthlyCost) setOfficialConnCost(groupsData.officialConnectionMonthlyCost);
      }    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar conexões');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle URL params from OAuth callback
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    const connectionId = searchParams.get('connectionId');
    const selection = searchParams.get('selection');
    const platform = searchParams.get('platform');
    const username = searchParams.get('username');

    if (connected) {
      toast.success(connected === 'whatsapp' && username ? `WhatsApp conectado com sucesso: ${username}` : `${connected} conectado com sucesso!`);
      setSearchParams({});
      fetchData();
    }

    if (error) {
      const msg = searchParams.get('msg');
      toast.error(msg ? decodeURIComponent(msg) : 'Erro ao conectar. Tente novamente.');
      setSearchParams({});
    }

    if (connectionId && selection) {
      setSelectionConnectionId(connectionId);
      setSelectionPlatform(platform || '');
      loadSelectionOptions(connectionId);
      setSearchParams({});
    }
  }, [searchParams]);

  // =========================================================================
  // GROUP HANDLERS
  // =========================================================================

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Digite um nome para o grupo');
      return;
    }
    try {
      setCreatingGroup(true);
      const res = await fetch(`${API_BASE_URL}/social-connections/groups`, {
        method: 'POST', headers,
        body: JSON.stringify({ name: newGroupName.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Grupo "${newGroupName.trim()}" criado!`);
        setNewGroupName('');
        fetchData();
      } else {
        toast.error(data.message || 'Erro ao criar grupo');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao criar grupo');
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleRenameGroup = async (groupId: string) => {
    if (!editingGroupName.trim()) return;
    try {
      const res = await fetch(`${API_BASE_URL}/social-connections/groups/${groupId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ name: editingGroupName.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Grupo renomeado');
        setEditingGroupId(null);
        fetchData();
      } else {
        toast.error(data.message || 'Erro ao renomear');
      }
    } catch (error: any) {
      toast.error('Erro ao renomear grupo');
    }
  };

  const handleDeleteGroup = async (group: Group) => {
    if (!confirm(`Excluir o grupo "${group.name}" e todas as suas ${group.connections.length} conexões? A cobrança mensal será interrompida.`)) return;
    try {
      setDeletingGroup(group.id);
      const res = await fetch(`${API_BASE_URL}/social-connections/groups/${group.id}`, {
        method: 'DELETE', headers,
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Grupo excluído');
        fetchData();
      } else {
        toast.error(data.message || 'Erro ao excluir grupo');
      }
    } catch (error: any) {
      toast.error('Erro ao excluir grupo');
    } finally {
      setDeletingGroup(null);
    }
  };

  // =========================================================================
  // CONNECTION HANDLERS
  // =========================================================================

  const loadSelectionOptions = async (connectionId: string) => {
    try {
      setLoadingSelection(true);
      setShowSelectionModal(true);
      const res = await fetch(`${API_BASE_URL}/social-connections/${connectionId}/selection-options`, { headers });
      const data = await res.json();
      if (data.alreadyConnected) {
        // O backend descobriu que a conta já existia na Zernio e ativou a conexão:
        // não há número a escolher, é só fechar o modal e recarregar.
        toast.success(data.message || 'Conexão ativada com sucesso!');
        setShowSelectionModal(false);
        setSelectionConnectionId(null);
        fetchData();
      } else if (data.success) {
        setSelectionOptions(data.data);
      } else {
        toast.error(data.message || 'Erro ao carregar opções');
        setShowSelectionModal(false);
      }
    } catch (error) {
      toast.error('Erro ao carregar opções de seleção');
      setShowSelectionModal(false);
    } finally {
      setLoadingSelection(false);
    }
  };

  // Trocar o metodo de OAuth de uma conta JA conectada. Nao desconecta: a conexao
  // nova nasce ao lado e absorve as automacoes da antiga no backend. Por isso
  // tambem nao passa pela confirmacao de creditos — nao e conexao nova, o ciclo
  // de cobranca e herdado.
  const handleSwitchLoginMethod = (platform: Platform, connection: Connection, groupId: string) => {
    setLoginMethodPlatform(platform);
    setLoginMethodGroupId(groupId);
    setLoginMethodReplaceId(connection.id);
    setLoginMethodChoice(platform.defaultLoginMethod || platform.loginMethods?.[0]?.id || null);
    setShowConnectModal(false);
  };

  const handleConnect = async (platform: Platform, groupId: string) => {
    // Escolha do metodo de OAuth (hoje so o Instagram tem). Vem ANTES da
    // confirmacao de creditos: a decisao muda o que a conta consegue fazer, e
    // perguntar depois de cobrar seria perguntar tarde demais.
    if (platform.loginMethods?.length) {
      setLoginMethodPlatform(platform);
      setLoginMethodGroupId(groupId);
      setLoginMethodChoice(platform.defaultLoginMethod || platform.loginMethods[0].id);
      setShowConnectModal(false);
      return;
    }
    return handleConnectWithMethod(platform, groupId, null);
  };

  const handleConnectWithMethod = async (platform: Platform, groupId: string, loginMethod: string | null, replaceConnectionId: string | null = null) => {
    if (platform.id === 'whatsapp') {
      setWhatsAppGroupId(groupId);
      setWhatsAppTab('redirect');
      setWhatsAppOnboarding(null);
      setWhatsAppCredentials({ accessToken: '', wabaId: '', phoneNumberId: '' });
      setShowWhatsAppModal(true);
      setShowConnectModal(false);
      return;
    }

    // Créditos (modelo por conexão): confirma a mensalidade ANTES de mandar o usuário
    // pro OAuth — sem isso, Instagram/Facebook/YouTube/etc. conectavam sem ver o custo.
    // (WhatsApp mostra o aviso no próprio modal acima.)
    if (perConnection && !replaceConnectionId) {
      requestConnectConfirm({
        title: `Conectar ${platform.name || platform.id}`,
        items: [{ featureKey: 'conn_social_official', qty: 1, label: 'conexão' }],
        note: 'Cobrança mensal recorrente: o valor é debitado no 1º ciclo após a ativação e renovado a cada mês. Sem saldo na renovação, a conexão é desativada. Você pode desconectar quando quiser.',
        confirmLabel: 'Conectar',
        onConfirm: () => { doConnectPlatform(platform, groupId, loginMethod, null); },
      });
      return;
    }

    doConnectPlatform(platform, groupId, loginMethod, replaceConnectionId);
  };

  const doConnectPlatform = async (platform: Platform, groupId: string, loginMethod: string | null = null, replaceConnectionId: string | null = null) => {

    if (platform.authType === 'credentials' && platform.id === 'bluesky') {
      setBlueskyGroupId(groupId);
      setShowBlueskyModal(true);
      setShowConnectModal(false);
      return;
    }

    if (platform.authType === 'telegram') {
      setTelegramGroupId(groupId);
      setTelegramCode('');
      setTelegramBotUsername('');
      setTelegramInstructions([]);
      setTelegramPolling(false);
      setTelegramConnected(false);
      setTelegramChatId('');
      setTelegramTab('code');
      setShowTelegramModal(true);
      setShowConnectModal(false);
      // Auto-generate code
      handleGetTelegramCode(groupId);
      return;
    }

    try {
      setConnecting(platform.id);
      const res = await fetch(`${API_BASE_URL}/social-connections/connect`, {
        method: 'POST', headers,
        body: JSON.stringify({
          platform: platform.id,
          groupId,
          ...(loginMethod ? { loginMethod } : {}),
          ...(replaceConnectionId ? { replaceConnectionId } : {}),
        }),
      });
      const data = await res.json();
      if (!data.success) {
        toast.error(data.message || 'Erro ao iniciar conexão');
        return;
      }
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao conectar');
    } finally {
      setConnecting(null);
      setShowConnectModal(false);
    }
  };

  const handleWhatsAppRedirect = async () => {
    if (!whatsAppGroupId) {
      toast.error('Selecione um grupo primeiro');
      return;
    }
    if (!whatsAppOnboarding) {
      toast.error('Escolha primeiro onde o seu número está hoje');
      return;
    }

    try {
      setConnecting('whatsapp');
      const res = await fetch(`${API_BASE_URL}/social-connections/connect/whatsapp?groupId=${encodeURIComponent(whatsAppGroupId)}&onboarding=${whatsAppOnboarding}`, { headers });
      const data = await res.json();

      if (data.success && data.authUrl) {
        window.location.href = data.authUrl;
        return;
      }

      toast.error(data.message || 'Erro ao iniciar autenticação do WhatsApp');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao iniciar autenticação do WhatsApp');
    } finally {
      setConnecting(null);
    }
  };

  const handleWhatsAppCredentials = async () => {
    const { accessToken, wabaId, phoneNumberId } = whatsAppCredentials;
    if (!accessToken || !wabaId || !phoneNumberId) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (!whatsAppGroupId) return;
    try {
      setConnecting('whatsapp');
      const res = await fetch(`${API_BASE_URL}/social-connections/connect/whatsapp/credentials`, {
        method: 'POST', headers,
        body: JSON.stringify({ groupId: whatsAppGroupId, accessToken, wabaId, phoneNumberId }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('WhatsApp Business conectado com sucesso!');
        setShowWhatsAppModal(false);
        fetchData();
      } else {
        toast.error(data.message || 'Erro ao conectar WhatsApp');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao conectar WhatsApp');
    } finally {
      setConnecting(null);
    }
  };

  const handleGetTelegramCode = async (groupId: string) => {
    try {
      setConnecting('telegram');
      const res = await fetch(`${API_BASE_URL}/social-connections/connect/telegram/code?groupId=${groupId}`, { headers });
      const data = await res.json();
      if (data.success) {
        setTelegramCode(data.data.code);
        setTelegramBotUsername(data.data.botUsername || '');
        setTelegramInstructions(data.data.instructions || []);
        // Start polling
        startTelegramPolling(data.data.code, groupId);
      } else {
        toast.error(data.message || 'Erro ao gerar código Telegram');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao gerar código Telegram');
    } finally {
      setConnecting(null);
    }
  };

  const startTelegramPolling = (code: string, groupId: string) => {
    setTelegramPolling(true);
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/social-connections/connect/telegram/poll?code=${code}&groupId=${groupId}`, { headers });
        const data = await res.json();
        if (data.success && data.data?.status === 'connected') {
          clearInterval(interval);
          setTelegramPolling(false);
          setTelegramConnected(true);
          toast.success('Telegram conectado com sucesso!');
          fetchData();
        } else if (data.data?.status === 'expired') {
          clearInterval(interval);
          setTelegramPolling(false);
          toast.error('Código expirado. Gere um novo código.');
        }
      } catch (e) { /* ignore poll errors */ }
    }, 3000);
    // Stop polling after 15 min
    setTimeout(() => { clearInterval(interval); setTelegramPolling(false); }, 15 * 60 * 1000);
  };

  const handleConnectTelegramByChatId = async () => {
    if (!telegramChatId.trim() || !telegramGroupId) return;
    try {
      setConnecting('telegram');
      const res = await fetch(`${API_BASE_URL}/social-connections/connect/telegram`, {
        method: 'POST', headers,
        body: JSON.stringify({ groupId: telegramGroupId, chatId: telegramChatId.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Telegram conectado com sucesso!');
        setShowTelegramModal(false);
        fetchData();
      } else {
        toast.error(data.message || 'Erro ao conectar Telegram');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao conectar Telegram');
    } finally {
      setConnecting(null);
    }
  };

  const handleConnectBluesky = async () => {
    if (!blueskyIdentifier || !blueskyPassword) {
      toast.error('Preencha o handle e a senha do app');
      return;
    }
    if (!blueskyGroupId) {
      toast.error('Selecione um grupo primeiro');
      return;
    }
    try {
      setConnecting('bluesky');
      const res = await fetch(`${API_BASE_URL}/social-connections/connect/bluesky`, {
        method: 'POST', headers,
        body: JSON.stringify({ identifier: blueskyIdentifier, password: blueskyPassword, groupId: blueskyGroupId }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Bluesky conectado com sucesso!');
        setShowBlueskyModal(false);
        setBlueskyIdentifier('');
        setBlueskyPassword('');
        setBlueskyGroupId(null);
        fetchData();
      } else {
        toast.error(data.message || 'Erro ao conectar Bluesky');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao conectar Bluesky');
    } finally {
      setConnecting(null);
    }
  };

  const handleSelectOption = async (optionId: string) => {
    if (!selectionConnectionId) return;
    try {
      setLoadingSelection(true);
      const res = await fetch(`${API_BASE_URL}/social-connections/${selectionConnectionId}/select`, {
        method: 'POST', headers,
        body: JSON.stringify({ selectionId: optionId }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Conexão ativada com sucesso!');
        setShowSelectionModal(false);
        setSelectionOptions([]);
        setSelectionConnectionId(null);
        fetchData();
      } else {
        toast.error(data.message || 'Erro ao completar seleção');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao completar seleção');
    } finally {
      setLoadingSelection(false);
    }
  };

  const handleDisconnect = async (connection: Connection) => {
    const platformName = platforms.find(p => p.id === connection.platform)?.name || connection.platform;
    const isPending = connection.status === 'pending';
    const confirmMsg = isPending
      ? `Cancelar a conexão pendente com ${platformName}?`
      : `Deseja desconectar ${platformName}${connection.accountUsername ? ` (@${connection.accountUsername})` : ''}?`;
    if (!confirm(confirmMsg)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/social-connections/${connection.id}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (data.success) {
        toast.success(isPending ? 'Conexão pendente cancelada' : 'Conexão removida');
        fetchData();
      } else {
        toast.error(data.message || 'Erro ao remover conexão');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao remover conexão');
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await fetch(`${API_BASE_URL}/social-connections/sync`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (data.success) {
        const { groupsCreated, connectionsCreated } = data.data || {};
        if (groupsCreated === 0 && connectionsCreated === 0) {
          toast.success('Tudo sincronizado! Nenhum dado novo encontrado.');
        } else {
          toast.success(data.message || 'Sincronização concluída!');
        }
        fetchData();
      } else {
        toast.error(data.message || 'Erro ao sincronizar');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao sincronizar conexões');
    } finally {
      setSyncing(false);
    }
  };

  const activeGroups = groups.filter(g => g.status === 'active');
  const perConnection = billingModel === 'per_connection';
  // Modelo por conexão: cobra-se por CONEXÃO OFICIAL ativa de qualquer rede social
  // (WhatsApp, Instagram, Facebook, YouTube, TikTok, etc.). Grupos são gratuitos.
  const activeOfficialCount = activeGroups.reduce(
    (sum, g) => sum + (g.connections || []).filter(c => c.status === 'active').length,
    0,
  );
  const totalMonthlyCost = perConnection ? activeOfficialCount * officialConnCost : activeGroups.length * monthlyCost;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando conexões...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {connectConfirmModal}
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">APIs Oficiais</h1>
          <p className="text-gray-600">
            {perConnection
              ? <>Crie grupos para organizar suas conexões de APIs oficiais. Grupos são gratuitos — a cobrança é por conexão: <strong>{officialConnCost} créditos/mês</strong> por conexão de rede social (WhatsApp, Instagram, Facebook, YouTube, TikTok, etc.).</>
              : <>Crie grupos para organizar suas conexões de APIs oficiais. Custo: <strong>{monthlyCost} créditos/mês</strong> por grupo.</>}
          </p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          title="Importar grupos e conexões já existentes na Late API para o banco local"
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg
            className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {syncing ? 'Sincronizando...' : 'Sincronizar'}
        </button>
      </div>

      {/* Summary Card */}
      {activeGroups.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-8 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Grupos Ativos</h3>
              <p className="text-3xl font-bold text-gray-900 mt-1">{activeGroups.length}</p>
            </div>
            <div className="text-right">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Custo Mensal</h3>
              <p className="text-3xl font-bold text-blue-600 mt-1">{totalMonthlyCost} créditos</p>
              <p className="text-xs text-gray-400 mt-1">
                {perConnection
                  ? `${activeOfficialCount} conexão(ões) ativa(s) × ${officialConnCost} créditos/mês`
                  : `${monthlyCost} créditos/grupo/mês`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Create Group */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-8 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Criar Novo Grupo</h2>
        <p className="text-sm text-gray-500 mb-4">
          {perConnection
            ? <>Cada grupo é um perfil independente onde você pode conectar as APIs oficiais que quiser. O grupo é <strong>gratuito</strong> — cada conexão de rede social custa <strong>{officialConnCost} créditos/mês</strong>.</>
            : <>Cada grupo é um perfil independente onde você pode conectar todas as APIs oficiais que quiser. Custo: <strong>{monthlyCost} créditos/mês</strong> por grupo.</>}
        </p>
        <div className="flex gap-3">
          <input
            type="text"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
            placeholder="Ex: Minha Marca, Empresa X, Projeto Y..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
          <button
            onClick={handleCreateGroup}
            disabled={creatingGroup || !newGroupName.trim()}
            className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creatingGroup ? 'Criando...' : 'Criar Grupo'}
          </button>
        </div>
      </div>

      {/* Groups */}
      {groups.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p className="text-lg font-medium">Nenhum grupo criado</p>
          <p className="text-sm mt-1">Crie um grupo acima para começar a conectar suas APIs oficiais.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => {
            const activeConns = group.connections.filter(c => c.status === 'active');
            const pendingConns = group.connections.filter(c => c.status === 'pending');
            const isEditing = editingGroupId === group.id;

            return (
              <div key={group.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {/* Group Header */}
                <div className="px-5 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-lg flex-shrink-0">
                      {group.name.charAt(0).toUpperCase()}
                    </div>
                    {isEditing ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="text"
                          value={editingGroupName}
                          onChange={(e) => setEditingGroupName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleRenameGroup(group.id)}
                          className="flex-1 px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                          autoFocus
                        />
                        <button onClick={() => handleRenameGroup(group.id)} className="text-xs text-blue-600 hover:text-blue-700 font-medium">Salvar</button>
                        <button onClick={() => setEditingGroupId(null)} className="text-xs text-gray-500 hover:text-gray-700">Cancelar</button>
                      </div>
                    ) : (
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{group.name}</h3>
                        <p className="text-xs text-gray-500">
                          {activeConns.length} conexão{activeConns.length !== 1 ? 'ões' : ''} ativa{activeConns.length !== 1 ? 's' : ''}
                          {group.nextChargeAt && ` · Próx. cobrança: ${new Date(group.nextChargeAt).toLocaleDateString('pt-BR')}`}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${group.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {group.status === 'active' ? 'Ativo' : group.status === 'disconnected' ? 'Desativado' : group.status}
                    </span>
                    <button
                      onClick={() => { setSelectedGroupId(group.id); setShowConnectModal(true); }}
                      className="text-sm font-medium px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      + Conectar
                    </button>
                    <button
                      onClick={() => { setEditingGroupId(group.id); setEditingGroupName(group.name); }}
                      className="text-gray-400 hover:text-gray-600 p-1"
                      title="Renomear"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5A3.003 3.003 0 0119.004 2h-6.992a3 3 0 00-5.996-1.49l-3.21 7.276a4.527 4.527 0 00-1.734 8.354 4.527 4.527 0 001.734 8.354L13.902 23.902a3 3 0 005.996 1.49h6.992a3 3 0 013.003-3.003l-7.276-3.21a4.506 4.506 0 00-8.354-1.734 4.503 4.503 0 00-8.354 1.734l-7.276 3.21A3 3 0 0013.902 23.902 3 3 0 0119.004 20.992a3 3 0 00-3.003-3.003" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group)}
                      disabled={deletingGroup === group.id}
                      className="text-gray-400 hover:text-red-600 p-1 disabled:opacity-50"
                      title="Excluir grupo"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Group Connections */}
                <div className="p-5">
                  {group.connections.length === 0 ? (
                    <div className="text-center py-6 text-gray-400">
                      <p className="text-sm">Nenhuma rede social conectada neste grupo.</p>
                      <button
                        onClick={() => { setSelectedGroupId(group.id); setShowConnectModal(true); }}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Conectar primeira rede social →
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Pending */}
                      {pendingConns.map((connection) => {
                        const platformConfig = platforms.find(p => p.id === connection.platform);
                        const colors = PLATFORM_COLORS[connection.platform] || { bg: 'bg-gray-600', text: 'text-white', border: 'border-gray-300', icon: '#666' };
                        return (
                          <div key={connection.id} className="rounded-lg border border-yellow-200 overflow-hidden">
                            <div className={`${colors.bg} ${colors.text} px-3 py-2 flex items-center gap-2 text-sm`}>
                              <PlatformIcon platform={connection.platform} size={16} />
                              <span className="font-medium">{platformConfig?.name || connection.platform}</span>
                              <span className="ml-auto bg-yellow-400 text-yellow-900 text-[10px] px-1.5 py-0.5 rounded-full font-medium">Pendente</span>
                            </div>
                            <div className="p-3">
                              <p className="text-xs text-gray-500 mb-2">Autenticação não concluída.</p>
                              <button onClick={() => handleDisconnect(connection)} className="text-xs text-gray-500 hover:text-red-600">Cancelar</button>
                            </div>
                          </div>
                        );
                      })}

                      {/* Active */}
                      {activeConns.map((connection) => {
                        const platformConfig = platforms.find(p => p.id === connection.platform);
                        const colors = PLATFORM_COLORS[connection.platform] || { bg: 'bg-gray-600', text: 'text-white', border: 'border-gray-300', icon: '#666' };
                        return (
                          <div key={connection.id} className="rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                            <div className={`${colors.bg} ${colors.text} px-3 py-2 flex items-center gap-2 text-sm`}>
                              <PlatformIcon platform={connection.platform} size={16} />
                              <span className="font-medium">{platformConfig?.name || connection.platform}</span>
                              <span className="ml-auto bg-white/20 text-[10px] px-1.5 py-0.5 rounded-full">Ativo</span>
                            </div>
                            <div className="p-3">
                              <div className="flex items-center gap-2 mb-2">
                                {connection.accountAvatar ? (
                                  <img src={connection.accountAvatar} alt="" className="w-8 h-8 rounded-full" />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                                    <PlatformIcon platform={connection.platform} size={14} />
                                  </div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 truncate">{connection.accountName || 'Conta conectada'}</p>
                                  {connection.accountUsername && (
                                    <p className="text-xs text-gray-500 truncate">@{connection.accountUsername}</p>
                                  )}
                                </div>
                              </div>

                              <div className="flex flex-wrap gap-1 mb-2">
                                {platformConfig?.supportsInbox && <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">DMs</span>}
                                {platformConfig?.supportsComments && <span className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded">Comentários</span>}
                                {platformConfig?.supportsReviews && <span className="text-[10px] bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded">Avaliações</span>}
                                {platformConfig?.supportsPosting && <span className="text-[10px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">Postagem</span>}
                                {platformConfig?.supportsAnalytics && <span className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">Analytics</span>}
                              </div>
                              {platformConfig?.loginMethods?.length && (
                                <button
                                  onClick={() => handleSwitchLoginMethod(platformConfig, connection, group.id)}
                                  className="w-full text-xs text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 border border-indigo-200 rounded-lg py-1.5 mb-1 transition-colors"
                                >
                                  Trocar forma de conexao
                                </button>
                              )}
                              <button
                                onClick={() => handleDisconnect(connection)}
                                className="w-full text-xs text-red-500 hover:text-red-700 hover:bg-red-50 border border-red-200 rounded-lg py-1.5 transition-colors"
                              >
                                Desconectar
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info Card */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-5">
        <div className="flex gap-3">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h3 className="font-medium text-blue-900 mb-1">Como funciona</h3>
            {perConnection ? (
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Grupos são <strong>gratuitos</strong> — servem para organizar suas conexões</li>
                <li>• <strong>Cada conexão de rede social</strong> (WhatsApp, Instagram, Facebook, YouTube, TikTok, etc.) custa <strong>{officialConnCost} créditos por mês</strong></li>
                <li>• Para conectar mais de uma conta da <strong>mesma plataforma</strong>, crie outro grupo</li>
                <li>• A cobrança é automática todo mês. Sem créditos, a conexão é desativada</li>
                <li>• Você pode desconectar a qualquer momento para parar a cobrança</li>
              </ul>
            ) : (
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Cada <strong>grupo</strong> custa <strong>{monthlyCost} créditos por mês</strong></li>
                <li>• Dentro de cada grupo, você pode conectar <strong>todas as APIs oficiais</strong> sem custo adicional</li>
                <li>• Para conectar mais de uma conta da <strong>mesma plataforma</strong>, crie outro grupo</li>
                <li>• A cobrança é automática todo mês. Sem créditos, o grupo é desativado</li>
                <li>• Você pode excluir um grupo a qualquer momento para parar a cobrança</li>
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Connect Platform Modal */}
      {showConnectModal && selectedGroupId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-semibold text-gray-900 text-lg">Conectar Rede Social</h3>
                <p className="text-sm text-gray-500">
                  Grupo: <strong>{groups.find(g => g.id === selectedGroupId)?.name}</strong>
                </p>
              </div>
              <button onClick={() => { setShowConnectModal(false); setSelectedGroupId(null); }} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {platforms.map((platform) => {
                const colors = PLATFORM_COLORS[platform.id] || { bg: 'bg-gray-600', text: 'text-white', border: 'border-gray-300', icon: '#666' };
                const isConnecting = connecting === platform.id;
                const group = groups.find(g => g.id === selectedGroupId);
                const alreadyConnected = group?.connections.some(c => c.platform === platform.id && c.status === 'active');

                return (
                  <button
                    key={platform.id}
                    onClick={() => handleConnect(platform, selectedGroupId)}
                    disabled={isConnecting || alreadyConnected}
                    className={`flex items-center gap-3 p-4 border rounded-xl text-left transition-all ${
                      alreadyConnected
                        ? 'border-green-200 bg-green-50 cursor-default'
                        : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                    } disabled:opacity-60`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${colors.bg} ${colors.text} flex items-center justify-center flex-shrink-0`}>
                      <PlatformIcon platform={platform.id} size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 text-sm">{platform.name}</p>
                      <p className="text-xs text-gray-500 truncate">{platform.description}</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {platform.supportsInbox && <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">DMs</span>}
                        {platform.supportsComments && <span className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded">Comentarios</span>}
                        {platform.supportsReviews && <span className="text-[10px] bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded">Avaliacoes</span>}
                        {platform.supportsPosting && <span className="text-[10px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">Postagem</span>}
                        {platform.supportsAnalytics && <span className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">Analytics</span>}
                      </div>
                    </div>
                    {alreadyConnected ? (
                      <span className="text-xs text-green-600 font-medium">Conectado</span>
                    ) : isConnecting ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                    ) : (
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Telegram Modal */}
      {showTelegramModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-sky-500 text-white flex items-center justify-center">
                <PlatformIcon platform="telegram" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Conectar Telegram</h3>
                <p className="text-sm text-gray-500">Canal ou grupo do Telegram</p>
              </div>
              <button onClick={() => setShowTelegramModal(false)} className="ml-auto text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1">
              <button onClick={() => setTelegramTab('code')} className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${ telegramTab === 'code' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700' }`}>Via Código</button>
              <button onClick={() => setTelegramTab('chatid')} className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${ telegramTab === 'chatid' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700' }`}>Via Chat ID</button>
            </div>

            {telegramTab === 'code' ? (
              <div>
                {telegramConnected ? (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="font-semibold text-gray-900">Telegram conectado!</p>
                    <button onClick={() => setShowTelegramModal(false)} className="mt-4 px-6 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600">Fechar</button>
                  </div>
                ) : telegramCode ? (
                  <div>
                    <p className="text-sm text-gray-600 mb-4">
                      Siga os passos abaixo para conectar seu canal ou grupo:
                    </p>
                    <ol className="space-y-2 mb-5">
                      {telegramInstructions.map((step, i) => (
                        <li key={i} className="text-sm text-gray-700 flex gap-2">
                          <span className="text-sky-500 font-bold flex-shrink-0">{i + 1}.</span>
                          <span>{step.replace(/^\d+\.\s*/, '')}</span>
                        </li>
                      ))}
                    </ol>
                    <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 mb-4">
                      <p className="text-xs text-sky-600 font-medium mb-1">Seu código de acesso:</p>
                      <div className="flex items-center gap-2">
                        <code className="text-lg font-bold text-sky-800 flex-1">{telegramCode}</code>
                        <button onClick={() => { navigator.clipboard.writeText(telegramCode); toast.success('Código copiado!'); }} className="text-xs text-sky-600 hover:text-sky-800 border border-sky-300 rounded px-2 py-1">Copiar</button>
                      </div>
                      {telegramBotUsername && <p className="text-xs text-sky-600 mt-2">Bot: @{telegramBotUsername}</p>}
                    </div>
                    {telegramPolling && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sky-500"></div>
                        Aguardando confirmação...
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Adicione <strong>@{telegramBotUsername || 'LateScheduleBot'}</strong> como administrador no seu canal/grupo, depois informe o Chat ID abaixo.
                </p>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chat ID</label>
                  <input
                    type="text"
                    value={telegramChatId}
                    onChange={(e) => setTelegramChatId(e.target.value)}
                    placeholder="Ex: -1001234567890 ou @meucanal"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 outline-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    ID numérico (ex: -1001234567890) ou username com @ (ex: @meucanal)
                  </p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setShowTelegramModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancelar</button>
                  <button
                    onClick={handleConnectTelegramByChatId}
                    disabled={connecting === 'telegram' || !telegramChatId.trim()}
                    className="flex-1 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 disabled:opacity-50"
                  >
                    {connecting === 'telegram' ? 'Conectando...' : 'Conectar'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-green-500 text-white flex items-center justify-center">
                <PlatformIcon platform="whatsapp" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Conectar WhatsApp Business</h3>
                <p className="text-sm text-gray-500">API Oficial do WhatsApp Business</p>
              </div>
              <button onClick={() => setShowWhatsAppModal(false)} className="ml-auto text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <ConnectionCostNotice featureKey="conn_social_official" className="mb-4" />

            {/* Tabs */}
            <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1">
              <button onClick={() => setWhatsAppTab('redirect')} className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${ whatsAppTab === 'redirect' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700' }`}>Login com Meta</button>
              <button onClick={() => setWhatsAppTab('credentials')} className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${ whatsAppTab === 'credentials' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700' }`}>Credenciais Manuais</button>
            </div>

            {whatsAppTab === 'redirect' ? (
              <div>
                <p className="text-sm font-medium text-gray-900 mb-1">Onde está o seu número hoje?</p>
                <p className="text-sm text-gray-600 mb-4">
                  A Meta abre uma tela diferente para cada caso. Se você escolher a opção errada,
                  o seu número simplesmente não vai aparecer na lista lá.
                </p>

                <div className="space-y-3 mb-5">
                  {/* Coexistence: número que roda no app WhatsApp Business do celular. */}
                  <button
                    type="button"
                    onClick={() => setWhatsAppOnboarding('business_app')}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                      whatsAppOnboarding === 'business_app'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                        whatsAppOnboarding === 'business_app' ? 'border-green-600 bg-green-600' : 'border-gray-300'
                      }`} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Está no aplicativo WhatsApp Business</p>
                        <p className="text-xs text-gray-600 mt-1">
                          É o número que você usa no celular, no app verde do WhatsApp Business.
                          Ele continua funcionando normalmente no seu aparelho e passa a funcionar
                          aqui também — as conversas ficam sincronizadas entre os dois.
                        </p>
                        <p className="text-xs text-amber-700 mt-2">
                          Atenção: nesse modo a Meta não libera o gerenciamento de grupos. Se você
                          usa a adição de contatos em grupos, prefira a outra opção.
                        </p>
                      </div>
                    </div>
                  </button>

                  {/* Embedded Signup padrão: portfólio de negócios → WABA → número. */}
                  <button
                    type="button"
                    onClick={() => setWhatsAppOnboarding('api')}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                      whatsAppOnboarding === 'api'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                        whatsAppOnboarding === 'api' ? 'border-green-600 bg-green-600' : 'border-gray-300'
                      }`} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">É um número de API oficial (ou vou criar um agora)</p>
                        <p className="text-xs text-gray-600 mt-1">
                          Escolha esta opção se o número já está em uma conta do WhatsApp Business
                          (WABA) dentro do seu Gerenciador de Negócios da Meta — inclusive se ele
                          está hoje em outro provedor e você quer trazê-lo para cá. Também é a
                          opção certa para cadastrar um número novo do zero.
                        </p>
                        <p className="text-xs text-gray-600 mt-2">
                          A Meta vai pedir o portfólio de negócios, depois a conta do WhatsApp
                          Business e por fim o número.
                        </p>
                      </div>
                    </div>
                  </button>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-5">
                  <ul className="text-sm text-green-800 space-y-1">
                    <li>✓ Você será redirecionado para a página oficial de autenticação</li>
                    <li>✓ Faça login com sua conta do Facebook</li>
                    {whatsAppOnboarding === 'business_app' ? (
                      <li>✓ Selecione o número que já usa no app WhatsApp Business</li>
                    ) : (
                      <li>✓ Selecione o portfólio de negócios, a conta do WhatsApp Business e o número</li>
                    )}
                    <li>✓ Ao final, você voltará automaticamente para esta tela</li>
                  </ul>
                </div>

                <button
                  onClick={handleWhatsAppRedirect}
                  disabled={connecting === 'whatsapp' || !whatsAppOnboarding}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {connecting === 'whatsapp' ? (
                    <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> Redirecionando...</>
                  ) : (
                    <><PlatformIcon platform="whatsapp" size={18} /> Conectar</>
                  )}
                </button>
                <p className="text-xs text-gray-400 mt-3 text-center">
                  {whatsAppOnboarding
                    ? 'Você retornará automaticamente após concluir a conexão.'
                    : 'Escolha uma das opções acima para continuar.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Insira as credenciais do seu WhatsApp Business Account diretamente do Meta Business Manager.</p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Access Token Permanente <span className="text-red-500">*</span></label>
                  <input
                    type="password"
                    value={whatsAppCredentials.accessToken}
                    onChange={(e) => setWhatsAppCredentials(p => ({ ...p, accessToken: e.target.value }))}
                    placeholder="EAAxxxx..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">WABA ID <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={whatsAppCredentials.wabaId}
                    onChange={(e) => setWhatsAppCredentials(p => ({ ...p, wabaId: e.target.value }))}
                    placeholder="123456789012345"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number ID <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={whatsAppCredentials.phoneNumberId}
                    onChange={(e) => setWhatsAppCredentials(p => ({ ...p, phoneNumberId: e.target.value }))}
                    placeholder="987654321098765"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none text-sm"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setShowWhatsAppModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancelar</button>
                  <button
                    onClick={handleWhatsAppCredentials}
                    disabled={connecting === 'whatsapp'}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {connecting === 'whatsapp' ? 'Conectando...' : 'Conectar'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bluesky Modal */}
      {loginMethodPlatform && loginMethodGroupId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white flex items-center justify-center">
                <PlatformIcon platform={loginMethodPlatform.id} size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {loginMethodReplaceId ? 'Trocar forma de conexao' : `Conectar ${loginMethodPlatform.name}`}
                </h3>
                <p className="text-sm text-gray-500">Escolha por onde autorizar. Isso muda o que a conta consegue fazer.</p>
              </div>
            </div>

            <div className="space-y-3">
              {loginMethodPlatform.loginMethods?.map(method => (
                <label
                  key={method.id}
                  className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${loginMethodChoice === method.id ? 'border-purple-400 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  <input
                    type="radio"
                    checked={loginMethodChoice === method.id}
                    onChange={() => setLoginMethodChoice(method.id)}
                    className="mt-1"
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      {method.label}
                      {method.recommended && (
                        <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">RECOMENDADO</span>
                      )}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">{method.description}</p>
                  </div>
                </label>
              ))}
            </div>

            {loginMethodReplaceId ? (
              <p className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 p-2 text-xs text-emerald-800">
                A conta atual so sai depois que a nova entrar. Suas automacoes de comentario sao
                transferidas automaticamente, e a mensalidade nao e cobrada de novo — o ciclo atual
                e herdado. Se o login falhar no meio, nada muda.
              </p>
            ) : (
              <p className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
                Da para trocar depois pelo botao "Trocar forma de conexao" no card da conta, sem
                perder automacoes.
              </p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => { setLoginMethodPlatform(null); setLoginMethodGroupId(null); setLoginMethodChoice(null); setLoginMethodReplaceId(null); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  const platform = loginMethodPlatform;
                  const groupId = loginMethodGroupId;
                  const choice = loginMethodChoice;
                  const replaceId = loginMethodReplaceId;
                  setLoginMethodPlatform(null);
                  setLoginMethodGroupId(null);
                  setLoginMethodChoice(null);
                  setLoginMethodReplaceId(null);
                  void handleConnectWithMethod(platform, groupId, choice, replaceId);
                }}
                disabled={!loginMethodChoice}
                className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {showBlueskyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-sky-500 text-white flex items-center justify-center">
                <PlatformIcon platform="bluesky" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Conectar Bluesky</h3>
                <p className="text-sm text-gray-500">Use uma senha de app do Bluesky</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Handle</label>
                <input
                  type="text"
                  value={blueskyIdentifier}
                  onChange={(e) => setBlueskyIdentifier(e.target.value)}
                  placeholder="seuhandle.bsky.social"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Senha do App</label>
                <input
                  type="password"
                  value={blueskyPassword}
                  onChange={(e) => setBlueskyPassword(e.target.value)}
                  placeholder="xxxx-xxxx-xxxx-xxxx"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Crie uma senha de app em Configurações → Senhas de App no Bluesky
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowBlueskyModal(false); setBlueskyIdentifier(''); setBlueskyPassword(''); setBlueskyGroupId(null); }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConnectBluesky}
                disabled={connecting === 'bluesky'}
                className="flex-1 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50"
              >
                {connecting === 'bluesky' ? 'Conectando...' : 'Conectar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selection Modal (Facebook Pages, LinkedIn Orgs, etc) */}
      {showSelectionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-10 h-10 rounded-xl ${PLATFORM_COLORS[selectionPlatform]?.bg || 'bg-gray-600'} text-white flex items-center justify-center`}>
                <PlatformIcon platform={selectionPlatform} size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {selectionPlatform === 'whatsapp' ? 'Selecionar Número' : 'Selecionar Conta'}
                </h3>
                <p className="text-sm text-gray-500">
                  {selectionPlatform === 'whatsapp'
                    ? 'Escolha o número do WhatsApp Business que será conectado'
                    : 'Escolha qual conta deseja conectar'}
                </p>
              </div>
            </div>

            {loadingSelection ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : selectionOptions.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Nenhuma conta encontrada</p>
            ) : (
              <div className="space-y-2">
                {selectionOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => handleSelectOption(option.id)}
                    className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors text-left"
                  >
                    {option.avatar ? (
                      <img src={option.avatar} alt="" className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                        <PlatformIcon platform={selectionPlatform} size={18} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">{option.name}</p>
                      {option.category && <p className="text-sm text-gray-500 truncate">{option.category}</p>}
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={() => { setShowSelectionModal(false); setSelectionOptions([]); setSelectionConnectionId(null); }}
              className="w-full mt-4 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
